/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 *
 * Configs are set in finsemble.servicesConfig.assimilation
 */
import _get from "lodash/get";
import { BaseService } from "../baseService";
import { NotificationClient, Notification, Action, ActionTypes } from "../../clients/notificationClient";
import { ConfigClient } from "../../clients/configClient";
import { ConfigUtilInstance as ConfigUtil } from "../../common/configUtil";
import { toast } from "../../common/toast";
import { FinsembleWindow } from "../../common/FinsembleWindow";
import { Logger } from "../../clients/logger";
import { System } from "../../common/system";
import SystemManagerClient from "../../common/systemManagerClient";
import AssimilationWindowManager from "./assimilationWindowManager";
import AssimilationHelper from "./helper";
import AssimilationSocketConnection from "./AssimilationSocketConnection";
import { IRouterClient, ResponderMessage, RouterClient } from "../../clients/routerClient";
import { StandardError } from "../../types";

Logger.start();

const MINIMUM_WIDTH = 100;
const MINIMUM_HEIGHT = 25;
const DEFAULT_THROTLE_LEVEL = 20;

type ConfigCache = Record<string, any>;

export type SocketConnectionStatus = {
	connected: boolean;
	error?: any;
};

/**
 * The Assimilation Service controls native window

 */
class AssimilationService extends BaseService {
	assimilationWindowManager: any = null;

	// This port we talk to assimilation on
	port: number = 8392;

	routerClient: IRouterClient;

	readyFunction?: null | Function = null;

	appAssets: Record<string, any> | null = null;

	assimilationManifest: any = null;

	fullManifest: any = null;

	configCache: ConfigCache = {};

	uuidByName: Record<string, any> = {};

	ws: AssimilationSocketConnection | null = null;

	version: string = "";

	assimilationHelper: AssimilationHelper | null = null;

	serverAddress: string = "";

	constructor(name: string) {
		super({
			name,
			startupDependencies: {},
			shutdownDependencies: {
				services: ["windowService"],
			},
		});

		this.routerClient = RouterClient;
		this.load = this.load.bind(this);
		this.getApplicationList = this.getApplicationList.bind(this);

		this.onBaseServiceReady(async (callback: Function) => {
			Logger.system.debug("assimilation.onBaseServiceReady");

			System.getFEAInfo((version) => {
				this.version = version.FEA;
			});

			RouterClient.addPubSubResponder(/.*humanInterface.*/, {});

			// dynamic configuration load should be completed before entering an earlyuser stage
			SystemManagerClient.waitForBootStage("earlyuser", "stageEntered", this.getApplicationList);

			const launchingAssimilationExe = await this.load();

			// If we're launcihg assimilation.exe then defer the callback (readyFunction will be called later)
			// If we're not launching assimilation.exe, then we're done - callback now
			if (launchingAssimilationExe) {
				this.readyFunction = callback;
			} else {
				callback();
			}
		});

		this.start();
	}

	/**
	 * Java applications send handles with "native@x" added to the front. Also it is possible to get handles with leading zeros removed. Fix so that the handle matches the value from assimilation.
	 * @param {*} receivedHandle
	 */
	convertHandle(receivedHandle: string) {
		let handle = receivedHandle.replace("native@0x", "").toUpperCase();
		handle = `00000000${handle}`;
		handle = handle.substr(handle.length - 8);
		return handle;
	}

	/**
	 * Setup our listeners within the service
	 */
	createRouterEndpoints() {
		// sends updated monitor info
		RouterClient.addResponder("Assimilation.setMonitorInfo", (err, message) => {
			let errorObject: StandardError | null = null;
			if (this.ws) {
				try {
					this.ws.setMonitorInfo(message?.data);
				} catch (error: any) {
					errorObject = new Error(`assimilation_setMonitorInfo_failed - ${error.message}`);
				}
			} else {
				errorObject = new Error(
					`assimilation_missing_socket_connection - Assimilation socket connection does not exist, cannot set Monitor Info`
				);
			}
			if (errorObject) {
				message?.sendQueryResponse(errorObject, null);
			} else {
				message?.sendQueryResponse(null, null);
			}
		});
		// set a window to top
		RouterClient.addResponder("Assimilation.alwaysOnTop", (err: any, message) => {
			this.assimilationWindowManager.alwaysOnTop(message);
			message?.sendQueryResponse(null, null);
		});

		// move a window
		RouterClient.addResponder("Assimilation.moveWindow", (err, message) => {
			this.assimilationWindowManager.moveWindow(message?.data, this.ws);
			message?.sendQueryResponse(null, null);
		});
		// Save the state of a window
		RouterClient.addListener("Assimilation.saveWindow", (err, message) => {
			this.assimilationWindowManager.saveWindow(message);
		});

		// show a window
		RouterClient.addResponder("Assimilation.showWindow", (err, message) => {
			this.assimilationWindowManager.showWindow(message);

			message?.sendQueryResponse(null, null);
		});
		// maximizes a window
		RouterClient.addResponder("Assimilation.maximizeWindow", (err, message) => {
			this.assimilationWindowManager.maximizeWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// restore a window
		RouterClient.addResponder("Assimilation.restoreWindow", (err, message) => {
			this.assimilationWindowManager.restoreWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// minimize a window
		RouterClient.addResponder("Assimilation.minimizeWindow", (err, message) => {
			this.assimilationWindowManager.minimizeWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// hide a window
		RouterClient.addResponder("Assimilation.hideWindow", (err, message) => {
			this.assimilationWindowManager.hideWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// dock window
		RouterClient.addResponder("Assimilation.dockWindow", (err, message) => {
			// This is here and not in assimilationWindowManager because
			this.assimilationWindowManager.dockWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// undock window
		RouterClient.addResponder("Assimilation.undockWindow", (err, message) => {
			this.assimilationWindowManager.undockWindow(message);
			message?.sendQueryResponse(null, null);
		});
		// bring window to front
		RouterClient.addResponder("Assimilation.bringToFront", (err, message) => {
			this.assimilationWindowManager.bringToFront(message);
			message?.sendQueryResponse(null, null);
		});
		// close a window
		RouterClient.addResponder("Assimilation.closeWindow", (err, message) => {
			this.assimilationWindowManager.closeWindow(message);
		});
		/**
		 *
		 * This is for java. Java will send us a handle and then we'll try to hook it and add it to our active windows.
		 *
		 */
		RouterClient.addListener("Assimilation.hookWindowByHandle", (err, message) => {
			this.assimilationWindowManager.updateSpawningWindowFromWindowObject(message); // Turned off for now since there were issues in the native service
		});

		/**
		 * responders
		 *
		 */

		// Gets the window location from assimilation if the window is outside finsemble or the force param is passed in.
		// Otherwise returns current location stored on the window object.
		RouterClient.addResponder("Assimilation.getWindowLocation", (err, message) => {
			if (!message?.data.windowName) return message?.sendQueryResponse(null, null);
			const requestedWindow = this.assimilationWindowManager.getWindowByName(message.data.windowName);

			if (!requestedWindow) return message?.sendQueryResponse("no window found");

			if (requestedWindow.outsideOfFinsemble || message.data.force) {
				const ref = `${Date.now()}_${Math.random()}`;
				RouterClient.addListener(`${ref}-Assimilation.boundsUpdate`, (error, response) => {
					message?.sendQueryResponse(null, { location: response?.data });
				});
				this.assimilationWindowManager.sendSocketMessage(
					JSON.stringify({
						ref,
						uuid: requestedWindow.uuid,
						topic: "getBounds",
					})
				);
			} else {
				message?.sendQueryResponse(null, {
					location: requestedWindow.location,
				});
			}
		});

		/**
		 * This comes from the launcher. This will spawn a  bundled asset
		 *
		 */
		RouterClient.addResponder("Assimilation.spawnNative", (err, message) => {
			if (message) {
				this.spawnAsset(this.constructNativeWindowParams(message), () => {
					message?.sendQueryResponse(err, true);
				});
			}
		});

		/**
		 * Response from WPF application about graceful shutdown
		 *
		 */
		RouterClient.addListener("Assimilation.windowClosed", (err, message) => {
			Logger.system.debug("APPLICATION LIFECYCLE:Assmilation:windowClosed", message);
		});

		// Java applications can directly register window handles via the router with the Finsemble java library.
		RouterClient.addListener("Finsemble.Assimilation.register", (err, message) => {
			const windowInfo = message?.data;
			const windowsAwaitingSpawn = this.assimilationWindowManager.awaitingAssimilationSpawn;
			let outsideOfFinsemble = true;
			for (const ref in windowsAwaitingSpawn) {
				if (windowsAwaitingSpawn[ref].name == windowInfo.windowName) {
					outsideOfFinsemble = false;
					if (windowInfo.windowHandle) {
						const handle = this.convertHandle(windowInfo.windowHandle);
						const windowObject = this.assimilationWindowManager.updateSpawningWindow(ref, handle);
						const bounds = {
							left: windowObject.descriptor?.left,
							top: windowObject.descriptor?.top,
							right: windowObject.descriptor?.right,
							bottom: windowObject.descriptor?.bottom,
							width: windowObject.descriptor?.width,
							height: windowObject.descriptor?.height,
						};
						this.assimilationWindowManager.handleSpawnComplete(windowObject, bounds); // this.assimilationWindowManager.allWindows[handle]
					} else {
						// This app doesn't have a window
						const windowObject = this.assimilationWindowManager.updateSpawningWindow(ref, null);
						this.assimilationWindowManager.handleSpawnComplete(windowObject, null);
					}
					break;
				}
			}

			if (outsideOfFinsemble) {
				const ref = `${Date.now()}_${Math.random()}`;
				RouterClient.query(
					"Finsemble.NewNativeWindow",
					{
						appUUID: ref,
						name: windowInfo.windowName,
						appName: windowInfo.appName,
					},
					async () => {
						if (windowInfo.windowHandle) {
							const { wrap: windowWrap } = await FinsembleWindow.getInstance({
								name: windowInfo.windowName,
							});

							const handle = this.convertHandle(windowInfo.windowHandle);
							windowWrap.uuid = handle;
							windowWrap.outsideOfFinsemble = true;

							this.assimilationWindowManager.activeWindows[handle] = windowWrap;

							// If the windowInfo has bounds, continue to handleSpawnComplete, otherwise
							// send a socket message to get the latest window bounds from assimilation
							// before completing the spawn process
							RouterClient.addListener(`${ref}-Assimilation.boundsUpdate`, (error, response) => {
								this.assimilationWindowManager.handleSpawnComplete(windowWrap, response?.data);
							});

							const { bounds } = windowInfo;
							if (bounds) {
								this.assimilationWindowManager.handleSpawnComplete(windowWrap, bounds);
							} else {
								this.assimilationWindowManager.sendSocketMessage(
									JSON.stringify({
										ref,
										uuid: windowWrap.uuid,
										topic: "getBounds",
									})
								);
							}
						}
					}
				);
			}
		});
	}

	/**
	 * Returns boolean indicating whether the title bar should be hidden for native apps.
	 * @private
	 * @param {boolean} isWPF indicates whether the app should be treated as a WPF component
	 * @param {object} params the parameters used to spawn the component
	 */
	shouldHideTitleBar(isWPF: boolean, params: Record<string, any>) {
		// always hide the title bar of WPF components
		if (isWPF) {
			return true;
		}
		// if this component has a custom config value for hiding the title bar, use that
		// otherwise, return the global config value
		return _get(
			params,
			"customData.foreign.services.assimilationService.hideTitleBar",
			this.assimilationHelper?.manifest.hideTitleBars
		);
	}

	/**
	 * Constructs all of the parameters necessary for creation of a native window.
	 * @private
	 * @param {*} message the message received requesting the creation of a native window
	 */
	constructNativeWindowParams(message: ResponderMessage) {
		let params = message.data;
		const isWPF = params.isWPF || _get(params, "customData.window.argumentsAsQueryString");

		params.hideTitleBar = this.shouldHideTitleBar(isWPF, params);

		if (isWPF) {
			params = { params, ...this.getArgumentsWPF(message) };
			// spawnDefaultViewer causes assimilation to use shell exec instead of createProcess so
			// that it can auto execute the system viewer for a specific filetype (e.g. a URL or JAR)
			params.spawnDefaultViewer = true;
		}

		this.constructPath(params);
		return params;
	}

	getArgumentsWPF(message: ResponderMessage) {
		const params = { ...message.data };
		params.generatedArguments = params.arguments || "";
		if (!this.uuidByName[params.name]) {
			this.uuidByName[params.name] = `uuid${Date.now()}_${Math.round(Math.random() * 10000)}`;
		}

		if (!params.uuid) {
			params.uuid = this.uuidByName[params.name];
		}

		const args = new Map([
			["finsembleWindowName", params.name],
			["componentType", params.customData.component.type],
			["uuid", this.uuidByName[params.name]],
			["left", params.defaultLeft],
			["top", params.defaultTop],
			["width", params.defaultWidth],
			["height", params.defaultHeight],
			["iac", true],
			["serverAddress", this.serverAddress],
		]);

		// argumentsAsQueryString provides the option to use a URL (e.g. for a custom protocol registered with the system - this is used by some customers in custom launchers)
		if (params.path && params.customData.window.argumentsAsQueryString) {
			args.forEach((value, key) => {
				params.generatedArguments = `${params.generatedArguments}&${key}=${value}`;
			});
		} else {
			args.forEach((value, key) => {
				params.generatedArguments = `${params.generatedArguments} "${key}=${value}"`;
			});
		}

		if (!params.alias && !params.path) {
			params.path = params.customData.window.path;
			params.alias = params.customData.window.alias;
		}

		// add componentType to params for publishing BootStatus when spawn complete
		params.componentType = params.customData.component.type;

		this.configCache[params.name] = params;
		return params;
	}

	/**
	 * Construct a valid URI from the configured path and arguments.
	 * This should ensure that:
	 * * Duplicates are not introduced
	 * * The URI generated is in a valid format
	 *
	 * @param params
	 * @returns void
	 */
	constructPath(params: Record<string, any>) {
		if (
			params.path === undefined ||
			(params.customData &&
				params.customData.window &&
				Boolean(params.customData.window.argumentsAsQueryString) === false)
		) {
			return;
		}
		const path = new URL(params.path);
		const generatedParams = new URLSearchParams(params.generatedArguments);
		const queryParams = new URLSearchParams("");
		const addEntryToQuery = function (entry: string[]) {
			queryParams.append(entry[0], entry[1]);
		};
		const entries = [...path.searchParams.entries(), ...generatedParams.entries()];
		entries.forEach(addEntryToQuery);

		path.search = queryParams.toString();
		params.generatedPath = path.toString();
		params.generatedArguments = "";
	}

	/**
	 * This will turn on our c++ application and connect (via websocket).
	 */
	launchAssimilationExe() {
		System.launchExternalProcess(
			{
				alias: "assimilation",
				arguments: `PORT=${this.port}`,
				listener: (event) => {
					// react to close event
					if (event.topic === "exited") {
						// your desired logic here
						Logger.system.log("APPLICATION LIFECYCLE:Exited Assimilation Process", event);
						Logger.system.debug("APPLICATION LIFECYCLE:Exited Assimilation Process");
					} else {
						Logger.system.log("APPLICATION LIFECYCLE:Running Assimilation Process", event);
						Logger.system.debug("APPLICATION LIFECYCLE:Running Assimilation Process");
					}
				},
			},
			(payload) => {
				// success
				Logger.system.log("APPLICATION LIFECYCLE:Assimilation process successfully launched");
				Logger.system.debug("APPLICATION LIFECYCLE:Assimilation process successfully launched", payload.uuid);
				this.connectSockets(); // Connect to socket
			},
			(err) => {
				this.notifyAssimilationLaunchFailure(err);
				if (this.readyFunction) this.readyFunction();
			}
		);
	}

	async getAllConfigs() {
		const { err, data: fullManifest } = await ConfigClient.getManifest();

		if (!err && fullManifest) {
			this.fullManifest = fullManifest;
			/* Assimilation config can come from the defaults (/finsemble/configs/core/services.json),
		client-configured servicesConfig, or in betaFeatures config (deprecated).  */
			const defaultAssimilation = fullManifest?.finsemble.services.assimilationService.config;
			const servicesConfigAssimilation = fullManifest?.finsemble.servicesConfig.assimilation;
			const betaAssimilation = fullManifest?.finsemble.betaFeatures.assimilation;
			if (betaAssimilation) {
				Logger.system.error("Assimilation is no longer a beta feature. Move assimilation under serviceConfigs.");
			}
			this.assimilationManifest = Object.assign(defaultAssimilation, servicesConfigAssimilation || betaAssimilation);
			this.appAssets = fullManifest?.appAssets ?? null;
			this.assimilationHelper = new AssimilationHelper({
				appAssets: fullManifest?.appAssets,
				manifest: this.assimilationManifest,
			});
			this.assimilationWindowManager = new AssimilationWindowManager(this.assimilationHelper);
			const defaultServerAddress = "ws://127.0.0.1:3376";
			this.serverAddress = ConfigUtil.getDefault(
				fullManifest,
				"fullManifest.finsemble.router.transportSettings.FinsembleTransport.serverAddress",
				defaultServerAddress
			);
		}
	}

	/**
	 * Our main load event. Returns true if we're launching assimilation.exe (and therefore need to wait for it to start)
	 */
	async load(): Promise<boolean> {
		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation:load");
		const self = this;
		await this.getAllConfigs();
		const data = this.assimilationManifest;
		if (data?.port) this.port = data.port;
		// If assimilation isn't enabled, log, CB, and exit.
		if (!data.enabled) {
			// ready = true;
			Logger.system.log("APPLICATION LIFECYCLE:Assimilation Not Enabled");
			return false;
		}
		// If not running on windows then disable assimilation
		if (navigator.platform.indexOf("Win") == -1) {
			// ready = true;
			Logger.system.warn(
				"APPLICATION LIFECYCLE:Assimilation disabled due to incompatible platform",
				navigator.platform
			);
			return false;
		}

		Logger.system.debug("APPLICATION LIFECYCLE:Assimilation: Is Enabled");
		// Helper to create a notification.

		const notify = (identifier: string, message: string) => {
			toast("dev", "ONCE-SINCE-STARTUP", identifier, message);
			Logger.system.error("APPLICATION LIFECYCLE:Assimilation Error:", identifier, message);
		};
		const foundAsset = this.assimilationHelper?.findAssetByAlias("assimilation");

		if (!foundAsset) {
			// If assimilation is enabled and we can't find the asset, notify the dev. Do not let startup proceed (we don't call CB in this block, so nothing that depends on assimilation will start).
			notify(
				"assimilationService-not-found",
				"Could not find assimilation asset. Ensure that appAssets is properly formatted in your application's manifest."
			);
			return false;
		}

		if (this.compareVersion("3.6.0", foundAsset.version) < 0) {
			// If they have an old version of assimilation, let them know, but let startup proceed.
			notify(
				"assimilationService-bad-version",
				`This version of ${
					this.fullManifest?.startup_app?.name || "Finsemble"
				} requires assimilation version >= 3.6.0. Check your manifest.`
			);
			return false;
		}

		this.assimilationWindowManager.setFloatingTitlebar(
			_get(self.fullManifest, "finsemble.Window Manager.floatingTitlebarComponent", "floatingTitlebar")
		);

		this.createRouterEndpoints();
		// a function to call once we've connected to assimilation
		Logger.system.log("APPLICATION LIFECYCLE:Assimilation Enabled");
		this.launchAssimilationExe(); // Turn on the application
		return true;
	}

	onLoggerStateChanged(state: Record<string, any>) {
		let message = {
			topic: "onLogLevel",
		};

		["Error", "Warn", "Log", "Info", "Debug", "Verbose"].forEach((logLevel) => {
			(message as any)[logLevel.toLowerCase()] = state.system[logLevel];
		});

		if (this.assimilationHelper) {
			this.ws?.send(this.assimilationHelper?.prepareAction(message));
		}
	}

	/**
	 * Socket connection handler function that will get the connection status from AssimilationSocketConnections.
	 */
	socketConnectionHandler(socketConnectionStatus: SocketConnectionStatus) {
		if (socketConnectionStatus.connected) {
			this.assimilationWindowManager.setWebsocket(this.ws);
			this.ws?.setThrottle(
				this.assimilationManifest?.throttle >= 0 ? this.assimilationManifest?.throttle : DEFAULT_THROTLE_LEVEL
			);
		} else {
			this.notifyAssimilationLaunchFailure(socketConnectionStatus.error);
		}

		this.onLoggerStateChanged(Logger.setting());

		if (this.readyFunction) this.readyFunction();
	}

	private notifyAssimilationLaunchFailure(error) {
		Logger.system.error("Assimilation.launchAssimilationExe Error:", error);
		// KB40615 Disable assimilation in the config so that claimed space can be disabled by a config
		// listener in dockingMain.maybeAssimilationFailed (otherwise it may cause issues with toolbar visibility,
		// which is only reset when monitor resize events are triggered by claimSpace)
		ConfigClient.setValue({ field: "finsemble.servicesConfig.servicesConfig.assimilation.enabled", value: false });
		const notification = new Notification();
		const dismissAction = new Action();
		dismissAction.buttonText = "Dismiss";
		dismissAction.type = ActionTypes.DISMISS;
		notification.title = "Critical Failure in Assimilation Service";
		notification.details =
			"Assimilation failed To launch. Native applications will not work. Check the Central Logger for additional details.";
		notification.actions = [dismissAction];
		NotificationClient.notify(notification);
	}

	/**
	 * Connects to the assimilation web socket
	 */
	connectSockets() {
		// Connect to the c++ app
		if (this.assimilationManifest?.port) {
			this.port = this.assimilationManifest.port;
		}
		this.ws = new AssimilationSocketConnection(this.assimilationManifest, this.assimilationWindowManager);
		this.ws.connect(this.socketConnectionHandler.bind(this));

		if (Logger.onStateChanged !== undefined) {
			Logger.onStateChanged((state: Record<string, any>) => {
				this.onLoggerStateChanged(state);
			});
		}
	}

	/**
	 * Compares two semantical version numbers. Returns 1 if the right is greater than the left. -1 otherwise, or zero if they are equal.
	 * @param {string} left
	 * @param {string} right
	 */
	compareVersion(left: string, right: string) {
		if (!left) return 1;
		if (!right) return -1;
		const l = left.split(".");
		const r = right.split(".");
		// iterate through and compare one tuple at a time (major, minor, patch, etc)
		for (let i = 0; i < Math.max(l.length, r.length); i++) {
			// If we've run out of tuples then the longer one is greater (by definition)
			if (i == l.length && i == r.length) return 0;
			if (i >= r.length) return -1;
			if (i >= l.length) return 1;

			// If the given tuple isn't equal, then we know which one is larger
			const tupleL = parseInt(l[i]);
			const tupleR = parseInt(r[i]);
			if (tupleL > tupleR) return -1;
			if (tupleR > tupleL) return 1;
		}
		return 0;
	}

	/**
	 * Spawn a component
	 */
	spawnAsset(params: Record<string, any>, cb: Function) {
		const identifier = {
			name: params.name,
			uuid: params.uuid,
			componentType: params.componentType,
			windowType: "NativeWindow",
			waitForReady: false,
		};
		return FinsembleWindow.getInstance(identifier, (err: StandardError, windowWrap: FinsembleWindow) => {
			params.minWidth = params.minWidth && params.minWidth > MINIMUM_WIDTH ? params.minWidth : MINIMUM_WIDTH;
			params.minHeight = params.minHeight && params.minHeight > MINIMUM_HEIGHT ? params.minHeight : MINIMUM_HEIGHT;
			params.maxWidth = params.maxWidth && params.maxWidth > MINIMUM_WIDTH ? params.maxWidth : Infinity;
			params.maxHeight = params.maxHeight && params.maxHeight > MINIMUM_HEIGHT ? params.maxHeight : Infinity;

			if (params.maxWidth < params.defaultWidth) {
				params.defaultWidth = params.maxWidth;
			}
			if (params.minWidth > params.defaultWidth) {
				params.defaultWidth = params.minWidth;
			}
			if (params.maxHeight < params.defaultHeight) {
				params.defaultHeight = params.maxHeight;
			}
			if (params.minHeight > params.defaultHeight) {
				params.defaultHeight = params.minHeight;
			}
			windowWrap.descriptor = params;
			this.assimilationWindowManager.spawnWindow(windowWrap, this.ws);
			return cb();
		});
	}

	getApplicationList() {
		if (this.ws) {
			this.ws.getApplicationList();
		}
	}
}

export const serviceInstance = new AssimilationService("assimilationService");
